import {
    AUTO_DELIVERY_DCX_ORDER_STATUS_TYPES,
    AUTO_DELIVERY_DCX_STATUS_TYPES,
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES,
} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import type {AutoDeliveryBase, AutoDeliveryOrder} from '@/types/icc/autoDeliveryDetails';

export type VisualState = 'done' | 'default' | 'error' | 'active';

export type DeliveryStepperRenderItem = {
    step: VisualState;
    connector?: Exclude<VisualState, 'active'>;
};

export type GetDeliveryStepperRenderModelParams = {
    status?: AutoDeliveryBase['status'];
    orderStatus?: AutoDeliveryOrder['status'];
    statusUpdateReason?: AutoDeliveryBase['statusUpdateReason'];
};

export type DeliveryStepperRenderModel = {
    steps: DeliveryStepperRenderItem[];
    activeStepIndex: number;
};

const TOTAL_STEPS = 3;
const LAST_STEP_INDEX = TOTAL_STEPS - 1;
const ERROR_REASONS = new Set<string>([
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_LSP_01,
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_LSP_02,
    AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.AD_ERROR_01,
]);

// TODO: replace ERR_AD_OOS_PARTIAL_01 with real flag once known
const PARTIAL_DELIVERY_REASONS = new Set<string>([AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_OOS_PARTIAL_01]);

const getOrderProgressIndex = (orderStatus?: string | null): number | null => {
    const normalizedOrderStatus = orderStatus?.toUpperCase();

    switch (normalizedOrderStatus) {
        case AUTO_DELIVERY_DCX_ORDER_STATUS_TYPES.COMPLETED:
            return 2;
        case AUTO_DELIVERY_DCX_ORDER_STATUS_TYPES.ON_ITS_WAY:
            return 1;
        case AUTO_DELIVERY_DCX_ORDER_STATUS_TYPES.PROCESSED:
            return 0;
        default:
            return null;
    }
};

const getActiveStepsModel = (orderProgressIndex: number): DeliveryStepperRenderModel => {
    const isCompleted = orderProgressIndex === LAST_STEP_INDEX;
    const activeStepIndex = Math.min(orderProgressIndex + 1, LAST_STEP_INDEX);
    const steps = Array.from({length: TOTAL_STEPS}, (_, index): DeliveryStepperRenderItem => {
        const step: DeliveryStepperRenderItem = {
            step:
                index <= orderProgressIndex ? 'done' : index === activeStepIndex && !isCompleted ? 'active' : 'default',
        };

        if (index < LAST_STEP_INDEX) {
            step.connector = index <= orderProgressIndex ? 'done' : 'default';
        }

        return step;
    });

    return {
        steps,
        activeStepIndex: isCompleted ? orderProgressIndex : activeStepIndex,
    };
};

const getFailedStepsModel = (): DeliveryStepperRenderModel => {
    return {
        steps: [{step: 'done', connector: 'done'}, {step: 'error', connector: 'error'}, {step: 'error'}],
        activeStepIndex: 1,
    };
};

export const getDeliveryStepperRenderModel = ({
    status,
    orderStatus,
    statusUpdateReason,
}: GetDeliveryStepperRenderModelParams): DeliveryStepperRenderModel | null => {
    const normalizedStatus = status?.toUpperCase();
    const normalizedStatusUpdateReason = statusUpdateReason?.toUpperCase();

    if (normalizedStatus !== AUTO_DELIVERY_DCX_STATUS_TYPES.ACTIVE) {
        return null;
    }

    if (!normalizedStatusUpdateReason || PARTIAL_DELIVERY_REASONS.has(normalizedStatusUpdateReason)) {
        const orderProgressIndex = getOrderProgressIndex(orderStatus);

        return orderProgressIndex === null ? null : getActiveStepsModel(orderProgressIndex);
    }

    if (normalizedStatusUpdateReason === AUTO_DELIVERY_DCX_UPDATE_REASON_STATUS_TYPES.ERR_AD_STOCK_01) {
        return null;
    }

    if (ERROR_REASONS.has(normalizedStatusUpdateReason)) {
        return getFailedStepsModel();
    }

    return null;
};
